import { useMemo } from "react";
import { Grid, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import IconComponent from "../../../components/ui/IconComponent";
import CustomPaper from "../../../_components/CustomPaper";
import AdronTypography from "../../../_components/AdronTypography";
import OverlayTooltip from "../../../components/OverlayTooltip/OverlayTooltip";
import TooltipDirectionContent from "../../../components/TooltipDirectionContent/TooltipDirectionContent";
import SwitchField from "../../../components/ui/inputs/SwitchField";
import FuturesBotRangePrice from "./FuturesBotRangePrice";
import { Bot, Colors, Direction, RangeStart } from "../../../_app/types";

interface Props {
  bot: Bot;
  onChangeDirection: (value: Direction) => void;
  onChangeMinPrice: (value: number) => void;
  onChangeMaxPrice: (value: number) => void;
  onChangeStartPrice: (value: number) => void;
  onChangePriceStep: (value: number) => void;
  switchRangeStartMode: (value: RangeStart) => void;
  rangeStartMode: RangeStart;
  onEdit: (value: boolean) => void;
}

const FuturesBotRangeSettings = ({
  bot,
  onChangeDirection,
  onChangeMaxPrice,
  onChangeMinPrice,
  onChangeStartPrice,
  onChangePriceStep,
  switchRangeStartMode,
  rangeStartMode,
  onEdit,
}: Props) => {
  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);

  const maxPrice = useMemo(() => {
    const allPrices = bot.customGridSettings.flatMap((item) => [item.openPrice, item.closePrice]);
    return Math.max(...allPrices);
  }, [bot.customGridSettings]);

  const minPrice = useMemo(() => {
    const allPrices = bot.customGridSettings.flatMap((item) => [item.openPrice, item.closePrice]);
    return Math.min(...allPrices);
  }, [bot.customGridSettings]);

  return (
    <>
      <Grid container direction="column" spacing={3} sx={{ paddingBottom: 3, paddingTop: 2 }}>
        <Grid item>
          <CustomPaper sx={{ padding: "12px !important" }}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={"3px"}
              sx={{ marginBottom: "5px" }}
            >
              <AdronTypography value="Direction" halfOpacity />
              <OverlayTooltip placement="top-end" title={<TooltipDirectionContent />}>
                <IconComponent iconName={"Question"} hasStroke />
              </OverlayTooltip>
            </Stack>
            <SwitchField
              value={bot.gridDirection}
              onChange={onChangeDirection}
              paramList={[
                {
                  label: "Short",
                  icon: <IconComponent iconName={"TradeDown"} hasFill />,
                  color: Colors.Destructive,
                },
                {
                  label: "Central",
                  icon: <IconComponent iconName={"TradeCenter"} hasStroke hasFill size={15} />,
                },
                {
                  label: "Long",
                  icon: <IconComponent iconName={"TradeUp"} hasFill />,
                  color: Colors.Success,
                },
              ]}
              disabled={CustomGridMode}
            />
          </CustomPaper>
        </Grid>
        <Grid item>
          <FuturesBotRangePrice
            bot={bot}
            minPrice={CustomGridMode ? minPrice : bot.minPrice}
            maxPrice={CustomGridMode ? maxPrice : bot.maxPrice}
            startPrice={bot.startPrice}
            onChangeMinPrice={onChangeMinPrice}
            onChangeMaxPrice={onChangeMaxPrice}
            onChangeStartPrice={onChangeStartPrice}
            onChangePriceStep={onChangePriceStep}
            rangeStartMode={rangeStartMode}
            switchRangeStartMode={switchRangeStartMode}
            onEdit={onEdit}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default FuturesBotRangeSettings;
